/**
 * Hook for loading and managing JTL channel descriptions.
 *
 * Fetches live descriptions from JTL, groups by SalesChannelId,
 * and provides a push mutation for saving changes back to JTL.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getProviderDescriptions,
  pushDescriptionToJTL,
  type JTLDescription,
  type PushDescriptionRequest,
} from '@/lib/api/descriptions'
import { MASTER_CHANNEL_ID } from '@/components/products/v2/tabs/content/channels/channel-constants'

export type GroupedDescriptions = Record<string, JTLDescription[]>

/**
 * Normalize a channel key to single-dash form. JTL GET descriptions use a
 * double-dash SalesChannelId for some marketplace channels (e.g. "11-8--1-13")
 * while the canonical GOKI channel_id is single-dash ("11-8-1-13"). Collapsing
 * "--" → "-" reconciles both namespaces. (Writes are normalized server-side.)
 */
export function normalizeChannelKey(id: string): string {
  return id.replaceAll('--', '-')
}

function groupByChannel(descriptions: JTLDescription[]): GroupedDescriptions {
  const grouped: GroupedDescriptions = {}
  for (const desc of descriptions) {
    const key = desc.SalesChannelId
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(desc)
  }
  return grouped
}

function extractLanguages(descriptions: JTLDescription[]): string[] {
  const langs = new Set(descriptions.map((d) => d.LanguageIso))
  return Array.from(langs).sort()
}

function getMasterData(grouped: GroupedDescriptions, lang: string): JTLDescription | undefined {
  const masterDescs = grouped['1-1-0'] ?? []
  return masterDescs.find((d) => d.LanguageIso === lang)
}

function getChannelData(
  grouped: GroupedDescriptions, channelId: string, lang: string,
): JTLDescription | undefined {
  // Robust lookup: direct key, then double-dash/single-dash reconciliation so a
  // canonical (single-dash) pill id still finds a double-dash grouped entry.
  const norm = normalizeChannelKey(channelId)
  const matchKey =
    grouped[channelId] ? channelId
    : grouped[norm] ? norm
    : Object.keys(grouped).find((k) => normalizeChannelKey(k) === norm)
  const descs = matchKey ? grouped[matchKey] : []
  return descs.find((d) => d.LanguageIso === lang)
}

function getChannelIds(grouped: GroupedDescriptions): string[] {
  return Object.keys(grouped).sort((a, b) => {
    if (a === MASTER_CHANNEL_ID) return -1
    if (b === MASTER_CHANNEL_ID) return 1
    return a.localeCompare(b)
  })
}

/**
 * Full set of selectable channel ids (single-dash, deduplicated, master
 * excluded) — the union of all active channels (from `channelNames`, which the
 * backend populates for ALL active channels) and channels that already have a
 * live description (`grouped`). This lets the UI offer text slots for channels
 * that have no description yet.
 */
export function buildAllChannelIds(
  channelNames: Record<string, string>,
  grouped: GroupedDescriptions,
): string[] {
  const ids = new Set<string>()
  for (const k of Object.keys(channelNames)) ids.add(normalizeChannelKey(k))
  for (const k of Object.keys(grouped)) ids.add(normalizeChannelKey(k))
  ids.delete(MASTER_CHANNEL_ID)
  return Array.from(ids).sort((a, b) => a.localeCompare(b))
}

export function useChannelDescriptions(productId: string) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['channel-descriptions', productId],
    queryFn: () => getProviderDescriptions(productId),
    enabled: !!productId,
    staleTime: 30_000,
  })

  const descriptions = query.data?.data ?? []
  const grouped = groupByChannel(descriptions)
  const languages = extractLanguages(descriptions)
  const channelNames = query.data?.channel_names ?? {}

  const pushMutation = useMutation({
    mutationFn: (data: PushDescriptionRequest) =>
      pushDescriptionToJTL(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['channel-descriptions', productId],
      })
      // Content blocks cache invalidieren damit Übersicht aktuell ist
      queryClient.invalidateQueries({
        queryKey: ['content-blocks-channels', productId],
      })
      queryClient.invalidateQueries({
        queryKey: ['content-blocks', productId],
      })
    },
  })

  const channelIds = getChannelIds(grouped)
  const allChannelIds = buildAllChannelIds(channelNames, grouped)

  const getMaster = (lang: string) => getMasterData(grouped, lang)
  const getChannel = (channelId: string, lang: string) => getChannelData(grouped, channelId, lang)

  return { query, grouped, languages, channelIds, allChannelIds, channelNames, pushMutation, getMaster, getChannel }
}
